import { useState, useMemo } from "react";

const PLANS = [
  {
    id: "accelerated",
    name: "Accelerated 5-Year",
    term: 5,
    description: "Aggressive payoff in just 5 years with higher monthly payments. Minimizes total interest paid.",
    icon: "⚡",
    color: "#C44536",
    pros: ["Lowest total interest of any plan", "Debt-free in 5 years", "Builds equity & credit faster"],
    cons: ["Highest monthly payments", "Less cash for other goals", "May strain tight budgets"],
  },
  {
    id: "standard",
    name: "Standard Repayment",
    term: 10,
    description: "Fixed monthly payments over 10 years. You'll pay the least interest overall.",
    icon: "📐",
    color: "#2D6A4F",
    pros: ["Lowest total interest paid", "Predictable fixed payments", "Fastest payoff"],
    cons: ["Highest monthly payment", "Less budget flexibility"],
  },
  {
    id: "graduated",
    name: "Graduated Repayment",
    term: 10,
    description: "Payments start low and increase every 2 years over 10 years. Good if you expect income growth.",
    icon: "📈",
    color: "#E07A5F",
    pros: ["Lower initial payments", "Payments grow with income", "Same 10-year timeline"],
    cons: ["More total interest than Standard", "Payment increases can surprise you"],
  },
  {
    id: "extended",
    name: "Extended Repayment",
    term: 25,
    description: "Fixed or graduated payments stretched over 25 years. Requires $30k+ in loans.",
    icon: "📏",
    color: "#6B5B95",
    pros: ["Much lower monthly payments", "More budget breathing room"],
    cons: ["Significantly more interest paid", "Very long repayment period", "Requires $30k+ balance"],
  },
  {
    id: "ibr",
    name: "Income-Based (IBR)",
    term: 20,
    description: "Payments capped at 10-15% of discretionary income. Forgiveness after 20-25 years.",
    icon: "💰",
    color: "#3D85C6",
    pros: ["Payments tied to ability to pay", "Loan forgiveness after 20-25 years", "Safety net if income drops"],
    cons: ["May pay more interest long-term", "Forgiven amount may be taxable", "Annual income recertification"],
  },
  {
    id: "paye",
    name: "PAYE / REPAYE (SAVE)",
    term: 20,
    description: "Pay As You Earn plans cap payments at 10% of discretionary income with forgiveness options.",
    icon: "🛡️",
    color: "#D4A373",
    pros: ["Lowest IDR payments (10%)", "Interest subsidy available", "Forgiveness after 20-25 years"],
    cons: ["Must demonstrate financial need (PAYE)", "Program changes possible", "Long timeline to forgiveness"],
  },
  {
    id: "pslf",
    name: "Public Service (PSLF)",
    term: 10,
    description: "Tax-free forgiveness after 120 qualifying payments while working for government or nonprofit.",
    icon: "🏛️",
    color: "#81B29A",
    pros: ["Tax-free forgiveness after 10 years", "Works with IDR plans", "Significant savings potential"],
    cons: ["Must work for qualifying employer", "Strict payment requirements", "Employment changes reset progress"],
  },
];

function calculatePayment(principal, annualRate, months) {
  if (annualRate === 0) return principal / months;
  const r = annualRate / 12;
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

function calculateAmortization(principal, annualRate, months) {
  const monthly = calculatePayment(principal, annualRate, months);
  let balance = principal;
  let totalInterest = 0;
  const schedule = [];
  for (let i = 1; i <= months; i++) {
    const interest = balance * (annualRate / 12);
    const principalPart = monthly - interest;
    balance -= principalPart;
    totalInterest += interest;
    if (i % 12 === 0 || i === months) {
      schedule.push({ month: i, balance: Math.max(0, balance), totalInterest });
    }
  }
  return { monthly, totalInterest, totalPaid: principal + totalInterest, schedule };
}

function formatCurrency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function BarChart({ data, maxVal, color, label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: 1 }}>{label}</span>
      {data.map((d, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#666", width: 40, textAlign: "right" }}>Yr {d.month / 12}</span>
          <div style={{ flex: 1, background: "#f0ede8", borderRadius: 4, height: 18, overflow: "hidden" }}>
            <div
              style={{
                width: `${(d.balance / maxVal) * 100}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                borderRadius: 4,
                transition: "width 0.6s ease",
              }}
            />
          </div>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#444", width: 75, textAlign: "right" }}>{formatCurrency(d.balance)}</span>
        </div>
      ))}
    </div>
  );
}

function PlanCard({ plan, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: isSelected ? `2px solid ${plan.color}` : "2px solid transparent",
        background: isSelected ? `${plan.color}0d` : "#faf8f5",
        borderRadius: 14,
        padding: "16px 18px",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.25s ease",
        boxShadow: isSelected ? `0 4px 20px ${plan.color}22` : "0 1px 4px rgba(0,0,0,0.04)",
        transform: isSelected ? "translateY(-2px)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 22 }}>{plan.icon}</span>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 15, color: isSelected ? plan.color : "#2c2c2c" }}>{plan.name}</span>
      </div>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#777", lineHeight: 1.5, margin: 0 }}>{plan.description}</p>
      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, background: `${plan.color}18`, color: plan.color, padding: "3px 8px", borderRadius: 6 }}>{plan.term} years</span>
      </div>
    </button>
  );
}

export default function StudentLoanExplorer() {
  const [loanAmount, setLoanAmount] = useState(35000);
  const [interestRate, setInterestRate] = useState(5.5);
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [comparePlan, setComparePlan] = useState("ibr");
  const [view, setView] = useState("explore"); // explore | compare

  const plan = PLANS.find((p) => p.id === selectedPlan);
  const plan2 = PLANS.find((p) => p.id === comparePlan);

  const calc1 = useMemo(() => calculateAmortization(loanAmount, interestRate / 100, plan.term * 12), [loanAmount, interestRate, plan]);
  const calc2 = useMemo(() => calculateAmortization(loanAmount, interestRate / 100, plan2.term * 12), [loanAmount, interestRate, plan2]);

  const inputStyle = {
    fontFamily: "'DM Mono', monospace",
    fontSize: 18,
    border: "none",
    borderBottom: "2px solid #d4d0cb",
    background: "transparent",
    padding: "6px 2px",
    width: "100%",
    color: "#2c2c2c",
    outline: "none",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FAF8F5", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;0,9..144,700;1,9..144,400&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: "#2c2c2c", padding: "48px 32px 40px", color: "#faf8f5" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 28 }}>🎓</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#aaa" }}>Student Loan Navigator</span>
          </div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 36, fontWeight: 700, margin: "8px 0 12px", lineHeight: 1.15 }}>
            Understand Your<br />
            <span style={{ color: "#81B29A" }}>Repayment Options</span>
          </h1>
          <p style={{ fontSize: 15, color: "#aaa", maxWidth: 520, lineHeight: 1.6, margin: 0 }}>
            Compare federal student loan repayment plans side-by-side. Enter your loan details below to see projected payments and total costs.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 32px 60px" }}>
        {/* Loan Inputs */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "28px 32px",
            marginTop: -24,
            boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 32,
          }}
        >
          <div>
            <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, color: "#999", display: "block", marginBottom: 8 }}>Loan Balance</label>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, color: "#aaa" }}>$</span>
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Math.max(1000, Number(e.target.value)))}
                style={inputStyle}
              />
            </div>
            <input type="range" min={5000} max={200000} step={1000} value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} style={{ width: "100%", marginTop: 8, accentColor: "#2D6A4F" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, color: "#999", display: "block", marginBottom: 8 }}>Interest Rate</label>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <input
                type="number"
                step={0.1}
                value={interestRate}
                onChange={(e) => setInterestRate(Math.max(0, Math.min(15, Number(e.target.value))))}
                style={inputStyle}
              />
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, color: "#aaa" }}>%</span>
            </div>
            <input type="range" min={0} max={12} step={0.1} value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} style={{ width: "100%", marginTop: 8, accentColor: "#E07A5F" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {["explore", "compare"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    background: view === v ? "#2c2c2c" : "#f0ede8",
                    color: view === v ? "#faf8f5" : "#777",
                    transition: "all 0.2s",
                  }}
                >
                  {v === "explore" ? "Explore Plans" : "Compare Two"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Explore View */}
        {view === "explore" && (
          <div style={{ marginTop: 36 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 32 }}>
              {PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} isSelected={selectedPlan === p.id} onClick={() => setSelectedPlan(p.id)} />
              ))}
            </div>

            {/* Detail Panel */}
            <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.05)" }}>
              <div style={{ background: plan.color, padding: "24px 32px", color: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 32 }}>{plan.icon}</span>
                  <div>
                    <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600, margin: 0 }}>{plan.name}</h2>
                    <p style={{ fontSize: 13, opacity: 0.85, margin: "4px 0 0" }}>{plan.description}</p>
                  </div>
                </div>
              </div>

              <div style={{ padding: 32 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginBottom: 32 }}>
                  {[
                    { label: "Monthly Payment", value: formatCurrency(calc1.monthly), sub: "estimated" },
                    { label: "Total Interest", value: formatCurrency(calc1.totalInterest), sub: `over ${plan.term} years` },
                    { label: "Total Cost", value: formatCurrency(calc1.totalPaid), sub: "principal + interest" },
                  ].map((item, i) => (
                    <div key={i} style={{ background: "#faf8f5", borderRadius: 12, padding: "20px 22px" }}>
                      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#999", display: "block", marginBottom: 6 }}>{item.label}</span>
                      <span style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700, color: plan.color }}>{item.value}</span>
                      <span style={{ fontSize: 11, color: "#aaa", display: "block", marginTop: 4 }}>{item.sub}</span>
                    </div>
                  ))}
                </div>

                <BarChart data={calc1.schedule} maxVal={loanAmount} color={plan.color} label="Remaining Balance Over Time" />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 28 }}>
                  <div>
                    <h4 style={{ fontFamily: "'Fraunces', serif", fontSize: 15, color: "#2D6A4F", margin: "0 0 10px" }}>✓ Advantages</h4>
                    {plan.pros.map((p, i) => (
                      <p key={i} style={{ fontSize: 13, color: "#555", margin: "6px 0", lineHeight: 1.5, paddingLeft: 12, borderLeft: "2px solid #81B29A" }}>{p}</p>
                    ))}
                  </div>
                  <div>
                    <h4 style={{ fontFamily: "'Fraunces', serif", fontSize: 15, color: "#E07A5F", margin: "0 0 10px" }}>✗ Considerations</h4>
                    {plan.cons.map((c, i) => (
                      <p key={i} style={{ fontSize: 13, color: "#555", margin: "6px 0", lineHeight: 1.5, paddingLeft: 12, borderLeft: "2px solid #E07A5F" }}>{c}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compare View */}
        {view === "compare" && (
          <div style={{ marginTop: 36 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {[
                { current: selectedPlan, setCurrent: setSelectedPlan, calc: calc1, planObj: plan, label: "Plan A" },
                { current: comparePlan, setCurrent: setComparePlan, calc: calc2, planObj: plan2, label: "Plan B" },
              ].map(({ current, setCurrent, calc, planObj, label }, idx) => (
                <div key={idx}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: "#999" }}>{label}</span>
                    <select
                      value={current}
                      onChange={(e) => setCurrent(e.target.value)}
                      style={{
                        flex: 1,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14,
                        fontWeight: 600,
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: `2px solid ${planObj.color}33`,
                        background: `${planObj.color}08`,
                        color: planObj.color,
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      {PLANS.map((p) => (
                        <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
                    <div style={{ background: planObj.color, padding: "18px 24px", color: "#fff" }}>
                      <span style={{ fontSize: 22 }}>{planObj.icon}</span>
                      <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: "6px 0 0" }}>{planObj.name}</h3>
                    </div>
                    <div style={{ padding: 24 }}>
                      {[
                        { label: "Monthly Payment", value: formatCurrency(calc.monthly) },
                        { label: "Total Interest", value: formatCurrency(calc.totalInterest) },
                        { label: "Total Paid", value: formatCurrency(calc.totalPaid) },
                        { label: "Repayment Term", value: `${planObj.term} years` },
                      ].map((item, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < 3 ? "1px solid #f0ede8" : "none" }}>
                          <span style={{ fontSize: 13, color: "#888" }}>{item.label}</span>
                          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, fontWeight: 500, color: planObj.color }}>{item.value}</span>
                        </div>
                      ))}

                      <div style={{ marginTop: 20 }}>
                        <BarChart data={calc.schedule} maxVal={loanAmount} color={planObj.color} label="Balance Paydown" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Summary */}
            <div style={{ background: "#fff", borderRadius: 16, padding: 28, marginTop: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, margin: "0 0 16px", color: "#2c2c2c" }}>Comparison Summary</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                {[
                  {
                    label: "Monthly Savings",
                    value: formatCurrency(Math.abs(calc1.monthly - calc2.monthly)),
                    note: `${calc1.monthly < calc2.monthly ? plan.name : plan2.name} saves more monthly`,
                    color: "#2D6A4F",
                  },
                  {
                    label: "Interest Difference",
                    value: formatCurrency(Math.abs(calc1.totalInterest - calc2.totalInterest)),
                    note: `${calc1.totalInterest < calc2.totalInterest ? plan.name : plan2.name} pays less interest`,
                    color: "#E07A5F",
                  },
                  {
                    label: "Total Cost Difference",
                    value: formatCurrency(Math.abs(calc1.totalPaid - calc2.totalPaid)),
                    note: `${calc1.totalPaid < calc2.totalPaid ? plan.name : plan2.name} costs less overall`,
                    color: "#6B5B95",
                  },
                ].map((s, i) => (
                  <div key={i} style={{ background: "#faf8f5", borderRadius: 12, padding: "18px 20px" }}>
                    <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#999", display: "block", marginBottom: 6 }}>{s.label}</span>
                    <span style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</span>
                    <span style={{ fontSize: 12, color: "#888", display: "block", marginTop: 6, lineHeight: 1.4 }}>{s.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ marginTop: 36, padding: "20px 24px", background: "#f0ede8", borderRadius: 12 }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#999", lineHeight: 1.7, margin: 0 }}>
            <strong style={{ color: "#888" }}>Disclaimer:</strong> These calculations are estimates for educational purposes. Actual payments may vary based on your specific loan terms, servicer, and eligibility. Income-driven plan payments depend on your income and family size. Consult your loan servicer or studentaid.gov for personalized information.
          </p>
        </div>
      </div>
    </div>
  );
}
